import { api } from "./client";

export interface ProfileDto {
  userId: string;
  email: string;
  displayName: string;
  jobTitle?: string | null;
  department?: string | null;
  phoneNumber?: string | null;
  bio?: string | null;
  linkedInUrl?: string | null;
  certifications?: string | null;
  photoUrl?: string | null;
  roleCodes: string[];
  profileCompletedAt?: string | null;
  createdAt: string;

  // Outbound-correspondence signature.
  signatureName?: string | null;
  signatureTitle?: string | null;
  signaturePhone?: string | null;
  signatureEmail?: string | null;
  signatureBlock?: string | null;

  /**
   * The RESOLVED block — what a recipient actually sees at the foot of a letter
   * or an email.
   *
   * Render this; never compose a preview from the fields above. The server owns
   * the one composition rule (override → the signature fields → the directory
   * identity), and a second rule here would make the preview a quiet lie the
   * moment the two drifted.
   */
  signatureBlockPreview?: string | null;

  /**
   * Where a reply to this user's outbound Phoenix email actually lands, and how
   * they are named on it — resolved by the same server call the send makes.
   *
   * Render these; never compute `signatureEmail ?? email` here. That would be
   * right today and free to drift, which is exactly how a send preview stops
   * matching what is sent without anyone noticing — the same argument as
   * `signatureBlockPreview` directly above.
   *
   * Null means the user has no address on file at all: replies then reach the
   * Phoenix mailbox, and a preview should say so rather than invent one.
   *
   * ⚠️ There is deliberately no `from`. From is the configured Phoenix sender
   * and its display name comes from the ACS domain configuration, not from the
   * application, so there is no truthful value to put here.
   */
  replyToEmail?: string | null;
  replyToDisplayName?: string | null;
}

export interface UpdateProfilePayload {
  displayName?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  phoneNumber?: string | null;
  bio?: string | null;
  linkedInUrl?: string | null;
  certifications?: string | null;
  timeZone?: string | null;
  signatureName?: string | null;
  signatureTitle?: string | null;
  signaturePhone?: string | null;
  signatureEmail?: string | null;
  signatureBlock?: string | null;
}

export interface ProfilePhotoUploadResult {
  photoUrl: string;
}

export const profileApi = {
  me: () => api.get<ProfileDto>("/profile"),
  get: (userId: string) => api.get<ProfileDto>(`/profile/${userId}`),
  update: (body: UpdateProfilePayload) => api.put<ProfileDto>("/profile", body),
  uploadPhoto: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<ProfilePhotoUploadResult>("/profile/photo", form);
  },
};
